import { supabase } from '../../../shared/lib/supabase';

/** Category values allowed in stat_types table */
export const STAT_TYPE_CATEGORIES = [
  'distribution',
  'attack',
  'defense',
  'goalkeeper',
  'duels',
  'freekick',
  'outside',
  'errors',
  'game_flow',
] as const;

/** Event result values allowed in stat_types table */
export const STAT_TYPE_EVENT_RESULTS = ['success', 'failure', 'neutral'] as const;

export type StatTypeCategory = (typeof STAT_TYPE_CATEGORIES)[number];
export type StatTypeEventResult = (typeof STAT_TYPE_EVENT_RESULTS)[number];

export interface StatType {
  id: string;
  category: string;
  stat_key: string;
  display_name: string;
  short_description: string | null;
  event_result: string;
  is_active: boolean | null;
  sort_order: number | null;
  created_at: string | null;
  emoji: string | null;
  is_calculated: boolean | null;
  visible_in_reports: boolean | null;
  top_stats: boolean | null;
}

export interface StatTypeCreateInput {
  category: string;
  stat_key: string;
  display_name: string;
  short_description?: string | null;
  event_result: string;
  is_active?: boolean | null;
  sort_order?: number | null;
  emoji?: string | null;
  is_calculated?: boolean | null;
  visible_in_reports?: boolean | null;
  top_stats?: boolean | null;
}

export interface StatTypeUpdateInput {
  category?: string;
  stat_key?: string;
  display_name?: string;
  short_description?: string | null;
  event_result?: string;
  is_active?: boolean | null;
  sort_order?: number | null;
  emoji?: string | null;
  is_calculated?: boolean | null;
  visible_in_reports?: boolean | null;
  top_stats?: boolean | null;
}

/** Legacy option type for dropdowns (subset of fields) */
export interface StatTypeOption {
  id: string;
  stat_key: string;
  display_name: string | null;
  short_description: string | null;
  is_calculated: boolean | null;
}

const TABLE_NAME = 'stat_types';

export class StatTypesService {
  async getAll(): Promise<StatType[]> {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('*')
      .order('sort_order', { ascending: true, nullsFirst: false })
      .order('stat_key', { ascending: true });

    if (error) {
      console.error('Error fetching stat types:', error);
      throw error;
    }
    return (data || []) as StatType[];
  }

  /** Legacy list for dropdowns (subset of fields). */
  async list(): Promise<StatTypeOption[]> {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('id, stat_key, display_name, short_description, is_calculated')
      .order('stat_key', { ascending: true });

    if (error) {
      console.error('Error fetching stat types:', error);
      throw error;
    }
    return (data || []) as StatTypeOption[];
  }

  async getById(id: string): Promise<StatType | null> {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching stat type:', error);
      throw error;
    }
    return data as StatType;
  }

  async create(input: StatTypeCreateInput): Promise<StatType> {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert([input])
      .select()
      .single();

    if (error) {
      console.error('Error creating stat type:', error);
      throw error;
    }
    return data as StatType;
  }

  async update(id: string, input: StatTypeUpdateInput): Promise<StatType> {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .update(input)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating stat type:', error);
      throw error;
    }
    return data as StatType;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from(TABLE_NAME).delete().eq('id', id);
    if (error) {
      console.error('Error deleting stat type:', error);
      throw error;
    }
  }
}

export const statTypesService = new StatTypesService();
